import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import IntelStage from "./IntelStage.jsx";
import PlanningStage from "./PlanningStage.jsx";
import CampaignStage from "./CampaignStage.jsx";
import DebriefStage from "./DebriefStage.jsx";
import "./BudgetCommander.css";

const STAGES = ["intro", "intel", "planning", "campaign", "debrief"];
const STAGE_LABELS = { intel: "Intel", planning: "Plan", campaign: "Campaign", debrief: "Debrief" };

export default function BudgetCommander() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("intro");
  const [intelResult, setIntelResult] = useState(null);
  const [plan, setPlan] = useState(null);
  const [campaignResult, setCampaignResult] = useState(null);

  const restart = () => {
    setIntelResult(null);
    setPlan(null);
    setCampaignResult(null);
    setStage("intro");
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <button onClick={() => navigate("/")} className="btn-ghost mb-6 !text-[8px]">
        ◀ BACK TO LEVELS
      </button>

      <div className="panel relative overflow-hidden p-6">
        <div className="mb-6">
          <p className="label-pixel text-magenta">Level 01</p>
          <h1 className="pixel-heading text-base text-mist sm:text-xl">Budget Commander</h1>
        </div>

        {stage !== "intro" && (
          <div className="mb-6 flex gap-1.5">
            {["intel", "planning", "campaign", "debrief"].map((s) => {
              const idx = STAGES.indexOf(s);
              const currentIdx = STAGES.indexOf(stage);
              const complete = idx < currentIdx;
              const active = s === stage;
              return (
                <div key={s} className="flex-1">
                  <div
                    className={`h-1.5 w-full border border-black/60 ${
                      complete ? "bg-lime" : active ? "bg-cyan" : "bg-panel2"
                    }`}
                  />
                  <p
                    className={`mt-1 text-center font-display text-[7px] uppercase ${
                      active ? "text-cyan" : complete ? "text-lime" : "text-mist/30"
                    }`}
                  >
                    {STAGE_LABELS[s]}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className="bc-panel-transition" key={stage}>
          {stage === "intro" && (
            <div className="text-center">
              <p className="mb-3 text-sm text-mist/80">
                Every dollar is a <span className="text-cyan">soldier</span>. Every budget is a{" "}
                <span className="text-cyan">battle plan</span>. Your job as Commander: give every
                soldier an order before the month starts, then hold the line when real life
                attacks the plan.
              </p>
              <p className="mb-6 text-xs text-mist/50">
                Three phases: review real spending data, build a zero-based budget, then live
                through a month of decisions that test whether it holds up.
              </p>
              <button onClick={() => setStage("intel")} className="btn-magenta">
                ▶ START MISSION
              </button>
            </div>
          )}

          {stage === "intel" && (
            <IntelStage
              onComplete={(result) => {
                setIntelResult(result);
                setStage("planning");
              }}
            />
          )}

          {stage === "planning" && (
            <PlanningStage
              onDeploy={(deployedPlan) => {
                setPlan(deployedPlan);
                setStage("campaign");
              }}
            />
          )}

          {stage === "campaign" && plan && (
            <CampaignStage
              plan={plan}
              onComplete={(result) => {
                setCampaignResult(result);
                setStage("debrief");
              }}
            />
          )}

          {stage === "debrief" && intelResult && plan && campaignResult && (
            <DebriefStage
              intelResult={intelResult}
              plan={plan}
              campaignResult={campaignResult}
              onRetry={restart}
            />
          )}
        </div>
      </div>
    </div>
  );
}
